import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ArticleCard from "@/components/ArticleCard";
import { SEO, BreadcrumbSchema } from "@/components/SEO";
import api from "@/lib/api";

export default function RubriquePage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rubrique, setRubrique] = useState(null);
  const [articles, setArticles] = useState([]);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const page = parseInt(searchParams.get("page") || "1");

  useEffect(() => {
    setLoading(true);
    api.get(`/rubriques/${slug}`)
      .then(async (res) => {
        setRubrique(res.data);
        const artRes = await api.get("/articles", { params: { rubrique: res.data.id, page, limit: 9 } });
        setArticles(artRes.data.articles);
        setPages(artRes.data.pages);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug, page]);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted w-24" />
          <div className="h-10 bg-muted w-1/2" />
          <div className="h-4 bg-muted w-2/3" />
        </div>
      </div>
    );
  }

  if (!rubrique) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 text-center">
        <h1 className="font-heading text-3xl mb-4">Rubrique non trouvée</h1>
        <Link to="/blog"><Button variant="outline" className="rounded-none">Retour au blog</Button></Link>
      </div>
    );
  }

  return (
    <>
      <SEO title={rubrique.name} description={`${rubrique.name} — OFALycée`} url={`/rubrique/${slug}`} />
      <BreadcrumbSchema items={[{ name: "Accueil", url: "/" }, { name: rubrique.name }]} />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="rubrique-page">
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-2">
            <Link to="/blog" className="text-sm font-mono tracking-widest uppercase text-muted-foreground hover:text-primary transition-colors">Blog</Link>
            <span className="text-muted-foreground/50">/</span>
            <span className="text-sm font-mono tracking-widest uppercase text-primary">{rubrique.name}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl">{rubrique.name}</h1>
          {rubrique.description && (
            <p className="text-muted-foreground mt-3 text-lg max-w-2xl">{rubrique.description}</p>
          )}
          <p className="text-sm text-muted-foreground mt-2">{rubrique.article_count || 0} article{(rubrique.article_count || 0) !== 1 ? "s" : ""}</p>
        </div>

        {articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-muted-foreground text-lg">Aucun article dans cette rubrique pour le moment.</p>
            <Link to="/blog"><Button variant="outline" className="rounded-none mt-4">Voir tous les articles</Button></Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {pages > 1 && (
          <div className="flex justify-center gap-2 mt-16" data-testid="pagination">
            {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                className="rounded-none w-10 h-10"
                onClick={() => setSearchParams({ page: p.toString() })}
                data-testid={`page-${p}`}
              >
                {p}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
